import { useMemo, useState } from "react";
import { Loader2, RefreshCw, Table2, X } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/layout/TopBar";
import { useSupportIssues, useSupportMetrics, useSyncSupportMetrics, type SupportIssue } from "@/hooks/useSupportMetrics";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

/**
 * Synnergie Support (Brett, 2026-09-29) — is the support load going down
 * or not? Three things, nothing more: SSE Bug / BreakFix created per
 * month, resolved per month, and what is open. Every number clicks
 * through to the tickets behind it. Views: one per year plus the trend
 * across years; type switch Break/Fix, Bugs, Both. Mirrored from Jira by
 * the worker every few hours; nobody runs JQL by hand.
 */

const TREND = "all";
const TYPES = [
  { key: "BreakFix", label: "Break/Fix" },
  { key: "Bug", label: "Bugs" },
  { key: "", label: "Both" },
] as const;

// Series colours — categorical slots 1 and 2, light/dark steps (dataviz palette).
const SERIES: Record<string, { light: string; dark: string }> = {
  Bug: { light: "#2a78d6", dark: "#3987e5" },
  BreakFix: { light: "#eb6834", dark: "#d95926" },
};
const FALLBACK = { light: "#1baf7a", dark: "#199e70" };
const TYPE_LABEL: Record<string, string> = { BreakFix: "Break/Fix", Bug: "Bugs" };
const typeLabel = (t: string) => TYPE_LABEL[t] ?? t;
const seriesColor = (type: string, dark: boolean) => (dark ? (SERIES[type] ?? FALLBACK).dark : (SERIES[type] ?? FALLBACK).light);
const useIsDark = () => typeof document !== "undefined" && document.documentElement.classList.contains("dark");

function monthLabel(m: string, withYear = false): string {
  const [y, mm] = m.split("-").map(Number);
  return new Date(y, mm - 1, 1).toLocaleDateString("en-US", { month: "short", year: withYear || mm === 1 ? "2-digit" : undefined });
}
const ym = (iso: string) => iso.slice(0, 7);
const monthEnd = (m: string) => { const [y, mm] = m.split("-").map(Number); return new Date(y, mm, 0, 23, 59, 59); };
const isOpen = (t: SupportIssue) => t.status_category !== "done" && !t.done_at && !t.archived_at;
const doneAt = (t: SupportIssue) => t.done_at ?? t.resolved_at;

type Drill = { kind: "created" | "resolved" | "open"; month?: string };

export function SynnergieSupport() {
  const [view, setView] = useState<string>(TREND);
  const [type, setType] = useState<string>("");
  const [table, setTable] = useState(false);
  const [drill, setDrill] = useState<Drill | null>(null);
  const summary = useSupportMetrics(type || undefined);
  const issues = useSupportIssues(type || undefined);
  const sync = useSyncSupportMetrics();
  const dark = useIsDark();

  const years = useMemo(() => Array.from(new Set((summary.data?.months ?? []).map((m) => m.month.slice(0, 4)))), [summary.data]);
  const axis = useMemo(() => {
    const all = (summary.data?.months ?? []).map((m) => m.month);
    return view === TREND ? all : all.filter((m) => m.startsWith(view));
  }, [summary.data, view]);
  const types = summary.data?.types ?? [];
  const rows = issues.data ?? [];

  // Everything below derives from the mirrored rows, so a click-through
  // list always matches the number it came from.
  const perMonth = useMemo(() => axis.map((m) => {
    const created = rows.filter((t) => ym(t.created_at) === m);
    const resolved = rows.filter((t) => doneAt(t) && ym(doneAt(t)!) === m);
    const end = monthEnd(m);
    const openEnd = rows.filter((t) => new Date(t.created_at) <= end && !(doneAt(t) && new Date(doneAt(t)!) <= end) && !(t.archived_at && new Date(t.archived_at) <= end));
    return {
      month: m,
      created: Object.fromEntries(types.map((tp) => [tp, created.filter((t) => t.issue_type === tp).length])),
      createdTotal: created.length,
      resolved: Object.fromEntries(types.map((tp) => [tp, resolved.filter((t) => t.issue_type === tp).length])),
      resolvedTotal: resolved.length,
      openEnd: openEnd.length,
    };
  }), [axis, rows, types]);
  const totals = useMemo(() => ({
    created: perMonth.reduce((s, m) => s + m.createdTotal, 0),
    resolved: perMonth.reduce((s, m) => s + m.resolvedTotal, 0),
    open: rows.filter(isOpen).length,
  }), [perMonth, rows]);

  const drillRows = useMemo<SupportIssue[]>(() => {
    if (!drill) return [];
    const inView = (iso: string) => view === TREND || iso.startsWith(view);
    if (drill.kind === "created") return rows.filter((t) => (drill.month ? ym(t.created_at) === drill.month : inView(t.created_at)));
    if (drill.kind === "resolved") return rows.filter((t) => doneAt(t) && (drill.month ? ym(doneAt(t)!) === drill.month : inView(doneAt(t)!)));
    if (drill.month) { const end = monthEnd(drill.month); return rows.filter((t) => new Date(t.created_at) <= end && !(doneAt(t) && new Date(doneAt(t)!) <= end) && !(t.archived_at && new Date(t.archived_at) <= end)); }
    return rows.filter(isOpen);
  }, [drill, rows, view]);
  // "Break/Fix Open Items", "Break/Fix Created in Jul 26" — the type is
  // named in the title, so the list needs no Type column (Brett, 2026-09-29).
  const typePrefix = types.length === 1 ? `${typeLabel(types[0])} ` : "";
  const drillTitle = !drill ? "" : drill.kind === "open"
    ? (drill.month ? `${typePrefix}Open at end of ${monthLabel(drill.month, true)}` : `${typePrefix}Open Items`)
    : `${typePrefix}${drill.kind === "created" ? "Created" : "Resolved"} ${drill.month ? `in ${monthLabel(drill.month, true)}` : view === TREND ? "all years" : `in ${view}`}`;

  const loading = summary.isLoading || issues.isLoading;
  const empty = summary.data && summary.data.months.length === 0;

  return (
    <>
      <TopBar
        title="Synnergie Support"
        subtitle={`${summary.data?.project_key ?? "SSE"} ${(types.length ? types : ["BreakFix"]).map(typeLabel).join(" + ")} support — created, resolved, and open, mirrored from Jira. Click any number for the tickets.`}
      >
        <button
          onClick={() => sync.mutate(undefined, { onSuccess: (d) => toast.success(`Synced ${d.synced} issues from Jira`), onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Sync failed") })}
          disabled={sync.isPending}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 transition-colors hover:bg-raised disabled:opacity-50"
          data-testid="support-sync"
        >
          {sync.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync from Jira
        </button>
      </TopBar>

      <div className="space-y-6 p-8" data-testid="synnergie-support">
        {loading && <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">Loading…</div>}
        {(summary.isError || issues.isError) && <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">Couldn't load: {((summary.error ?? issues.error) as Error)?.message}</div>}
        {empty && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted" data-testid="support-empty">
            Nothing mirrored yet. Click <strong>Sync from Jira</strong>, or wait for the worker's next pass.
          </div>
        )}

        {summary.data && issues.data && !empty && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              {/* One tracked type in the data (Bugs were folded into Break/Fix, 2026-09-29) → no switch to show.
                  Judged on the unfiltered view only, so a narrowed view can always widen back to Both. */}
              {(type !== "" || types.length > 1) && (
              <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-xs" role="group" aria-label="Type">
                {TYPES.map((tp) => (
                  <button key={tp.key || "both"} onClick={() => { setType(tp.key); setDrill(null); }} className={cn("rounded-md px-3 py-1.5 font-medium transition-colors", type === tp.key ? "bg-brand-navy-600 text-white shadow-sm" : "text-ink-3 hover:bg-raised")} data-testid={`type-${tp.key || "both"}`}>{tp.label}</button>
                ))}
              </div>
              )}
              <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-xs" role="group" aria-label="View">
                {years.map((y) => (
                  <button key={y} onClick={() => { setView(y); setDrill(null); }} className={cn("rounded-md px-3 py-1.5 font-medium transition-colors", view === y ? "bg-brand-navy-600 text-white shadow-sm" : "text-ink-3 hover:bg-raised")} data-testid={`range-${y}`}>{y}</button>
                ))}
                <button onClick={() => { setView(TREND); setDrill(null); }} className={cn("rounded-md px-3 py-1.5 font-medium transition-colors", view === TREND ? "bg-brand-navy-600 text-white shadow-sm" : "text-ink-3 hover:bg-raised")} data-testid="range-all">
                  Trend {years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : ""}
                </button>
              </div>
              <button onClick={() => setTable((v) => !v)} className={cn("flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-medium transition-colors", table ? "bg-brand-navy-600 text-white" : "bg-surface text-ink-3 hover:bg-raised")} data-testid="support-table-toggle">
                <Table2 className="h-3.5 w-3.5" /> {table ? "Charts" : "Table"}
              </button>
              <span className="ml-auto text-[11px] text-ink-muted">
                {summary.data.synced_at ? `Mirrored ${new Date(summary.data.synced_at).toLocaleString()}` : "Not yet mirrored"} · since {summary.data.since}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="support-tiles">
              <Tile label="Created" value={totals.created} note={view === TREND ? "all years" : `in ${view}`} active={drill?.kind === "created" && !drill.month} onClick={() => setDrill({ kind: "created" })} testId="tile-created" />
              <Tile label="Resolved" value={totals.resolved} note={view === TREND ? "all years" : `in ${view}`} active={drill?.kind === "resolved" && !drill.month} onClick={() => setDrill({ kind: "resolved" })} testId="tile-resolved" />
              <Tile label="Open now" value={totals.open} note="not started or in progress" tone={totals.open > 0 ? "amber" : "ok"} active={drill?.kind === "open" && !drill.month} onClick={() => setDrill({ kind: "open" })} testId="tile-open" />
            </div>

            {table ? (
              <div className="overflow-x-auto rounded-2xl border border-line bg-surface" data-testid="support-table">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                    <th className="px-4 py-2">Month</th>{types.map((tp) => <th key={"c" + tp} className="px-4 py-2 text-right">Created {tp}</th>)}<th className="px-4 py-2 text-right">Created</th>{types.map((tp) => <th key={"r" + tp} className="px-4 py-2 text-right">Resolved {tp}</th>)}<th className="px-4 py-2 text-right">Resolved</th><th className="px-4 py-2 text-right">Open at end</th>
                  </tr></thead>
                  <tbody>
                    {perMonth.map((m) => (
                      <tr key={m.month} className="border-t border-line-soft">
                        <td className="px-4 py-1.5 text-ink">{monthLabel(m.month, true)}</td>
                        {types.map((tp) => <td key={"c" + tp} className="px-4 py-1.5 text-right tabular-nums text-ink-2">{m.created[tp] ?? 0}</td>)}
                        <td className="px-4 py-1.5 text-right tabular-nums text-ink"><button className="hover:underline" onClick={() => setDrill({ kind: "created", month: m.month })}>{m.createdTotal}</button></td>
                        {types.map((tp) => <td key={"r" + tp} className="px-4 py-1.5 text-right tabular-nums text-ink-2">{m.resolved[tp] ?? 0}</td>)}
                        <td className="px-4 py-1.5 text-right tabular-nums text-ink"><button className="hover:underline" onClick={() => setDrill({ kind: "resolved", month: m.month })}>{m.resolvedTotal}</button></td>
                        <td className="px-4 py-1.5 text-right tabular-nums text-ink"><button className="hover:underline" onClick={() => setDrill({ kind: "open", month: m.month })}>{m.openEnd}</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid gap-6 xl:grid-cols-3">
                <Card title="Created per month" sub="Click a bar for that month's tickets.">
                  <StackedBars months={perMonth} types={types} dark={dark} get={(m, tp) => m.created[tp] ?? 0} total={(m) => m.createdTotal} onPick={(m) => setDrill({ kind: "created", month: m })} testId="chart-created" />
                </Card>
                <Card title="Resolved per month" sub="Click a bar for that month's tickets.">
                  <StackedBars months={perMonth} types={types} dark={dark} get={(m, tp) => m.resolved[tp] ?? 0} total={(m) => m.resolvedTotal} onPick={(m) => setDrill({ kind: "resolved", month: m })} testId="chart-resolved" />
                </Card>
                <Card title="Open at month end" sub="Not yet resolved at the end of each month. Click a point.">
                  <LineChart months={perMonth} color={seriesColor("Bug", dark)} get={(m) => m.openEnd} onPick={(m) => setDrill({ kind: "open", month: m })} testId="line-open_end" />
                </Card>
              </div>
            )}

            {drill && (
              <section className="rounded-2xl border border-line bg-surface p-5 shadow-sm" data-testid="support-drill">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-ink">{drillTitle} <span className="ml-1 rounded-full bg-raised px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-ink-muted">{drillRows.length}</span></h2>
                  <button onClick={() => setDrill(null)} className="rounded p-1 text-ink-subtle hover:bg-raised hover:text-ink" aria-label="Close list"><X className="h-4 w-4" /></button>
                </div>
                {drillRows.length === 0 ? <p className="text-sm text-ink-muted">No tickets.</p> : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle"><th className="py-1.5">Ticket</th><th className="py-1.5">Status</th><th className="py-1.5 text-right">Created</th><th className="py-1.5 text-right">Resolved</th><th className="py-1.5 text-right">Days in progress</th></tr></thead>
                    <tbody>
                      {drillRows.slice().sort((a, b) => b.created_at.localeCompare(a.created_at)).map((t) => (
                        <tr key={t.issue_key} className="border-t border-line-soft">
                          <td className="py-1.5"><a href={`https://etedevops.atlassian.net/browse/${t.issue_key}`} target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-brand-navy-600 dark:text-brand-navy-300 hover:underline">{t.issue_key}</a> <span className="text-ink-2">{t.summary}</span></td>
                          <td className="whitespace-nowrap py-1.5 text-ink-3">{t.status ?? "—"}{t.archived_at && <span className="ml-1.5 rounded bg-raised px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-muted" title={`Archived in Jira ${formatDate(t.archived_at)}`}>archived</span>}</td>
                          <td className="whitespace-nowrap py-1.5 pl-4 text-right tabular-nums text-ink-2">{formatDate(t.created_at)}</td><td className="whitespace-nowrap py-1.5 pl-4 text-right tabular-nums text-ink-2">{formatDate(doneAt(t))}</td>
                          <td className="py-1.5 text-right tabular-nums text-ink-2">{t.days_in_progress ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Tile({ label, value, note, tone, active, onClick, testId }: { label: string; value: number | string; note?: string; tone?: "amber" | "ok"; active?: boolean; onClick: () => void; testId: string }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={cn("rounded-2xl border bg-surface p-4 text-left shadow-sm transition-all hover:shadow-md", active ? "border-brand-navy-400 ring-2 ring-brand-navy-400/40" : "border-line")} title="Show the tickets">
      <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">{label}</div>
      <div className={cn("mt-1 text-3xl font-extrabold tabular-nums leading-none", tone === "amber" ? "text-amber-700 dark:text-amber-300" : tone === "ok" ? "text-emerald-700 dark:text-emerald-300" : "text-ink")}>{value}</div>
      {note && <div className="mt-1.5 text-[11px] text-ink-muted">{note}</div>}
    </button>
  );
}

function Card({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      {sub && <p className="mb-3 text-[11px] text-ink-muted">{sub}</p>}
      {children}
    </section>
  );
}

// ---- charts (plain SVG, one y-axis each) ---------------------------------

const W = 640, H = 220, PAD = { l: 34, r: 8, t: 10, b: 26 };
type PM = { month: string; created: Record<string, number>; createdTotal: number; resolved: Record<string, number>; resolvedTotal: number; openEnd: number };

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((c) => n <= c) ?? 10;
  return step * p;
}

function StackedBars({ months, types, dark, get, total, onPick, testId }: { months: PM[]; types: string[]; dark: boolean; get: (m: PM, tp: string) => number; total: (m: PM) => number; onPick: (month: string) => void; testId: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...months.map(total)));
  const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b;
  const bw = Math.max(4, Math.min(34, (iw / Math.max(1, months.length)) * 0.62));
  const x = (i: number) => PAD.l + (iw / Math.max(1, months.length)) * (i + 0.5) - bw / 2;
  const y = (v: number) => PAD.t + ih - (v / max) * ih;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const hm = hover != null ? months[hover] : null;
  return (
    <div className="relative" data-testid={testId}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
        {ticks.map((tv) => (<g key={tv}><line x1={PAD.l} x2={W - PAD.r} y1={y(tv)} y2={y(tv)} className="stroke-line-soft" strokeWidth={1} /><text x={PAD.l - 6} y={y(tv) + 3} textAnchor="end" className="fill-ink-subtle" fontSize={9}>{tv}</text></g>))}
        {months.map((m, i) => {
          let acc = 0;
          return (
            <g key={m.month} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => onPick(m.month)} className="cursor-pointer" data-month={m.month}>
              <rect x={x(i) - 6} y={PAD.t} width={bw + 12} height={ih} fill="transparent" />
              {types.map((tp, ti) => {
                const v = get(m, tp); if (!v) return null;
                const top = y(acc + v), bottom = y(acc); acc += v;
                return <rect key={tp} x={x(i)} y={top} width={bw} height={Math.max(0, bottom - top - (ti > 0 ? 2 : 0))} rx={ti === types.length - 1 ? 3 : 0} fill={seriesColor(tp, dark)} opacity={hover == null || hover === i ? 1 : 0.45} />;
              })}
              {(months.length <= 14 || i % 3 === 0) && <text x={x(i) + bw / 2} y={H - 8} textAnchor="middle" className="fill-ink-subtle" fontSize={9}>{monthLabel(m.month, months.length > 14)}</text>}
            </g>
          );
        })}
      </svg>
      <Legend items={types.map((tp) => ({ label: typeLabel(tp), color: seriesColor(tp, dark) }))} />
      {hm && (
        <div className="pointer-events-none absolute right-2 top-1 rounded-md border border-line bg-surface px-2.5 py-1.5 text-[11px] shadow" data-testid="chart-tooltip">
          <div className="font-semibold text-ink">{monthLabel(hm.month, true)} · {total(hm)}</div>
          {types.map((tp) => <div key={tp} className="text-ink-2"><span className="mr-1 inline-block h-2 w-2 rounded-sm" style={{ background: seriesColor(tp, dark) }} />{typeLabel(tp)} {get(hm, tp)}</div>)}
        </div>
      )}
    </div>
  );
}

function LineChart({ months, color, get, onPick, testId }: { months: PM[]; color: string; get: (m: PM) => number; onPick: (month: string) => void; testId: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...months.map(get)));
  const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b;
  const x = (i: number) => PAD.l + (iw / Math.max(1, months.length - 1 || 1)) * i;
  const y = (v: number) => PAD.t + ih - (v / max) * ih;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const d = months.map((m, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(get(m))}`).join(" ");
  const hm = hover != null ? months[hover] : null;
  return (
    <div className="relative" data-testid={testId}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
        {ticks.map((tv) => (<g key={tv}><line x1={PAD.l} x2={W - PAD.r} y1={y(tv)} y2={y(tv)} className="stroke-line-soft" strokeWidth={1} /><text x={PAD.l - 6} y={y(tv) + 3} textAnchor="end" className="fill-ink-subtle" fontSize={9}>{tv}</text></g>))}
        <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={PAD.t + ih} className="stroke-ink-subtle" strokeWidth={1} strokeDasharray="3 3" />}
        {months.map((m, i) => (
          <g key={m.month} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => onPick(m.month)} className="cursor-pointer" data-month={m.month}>
            <rect x={x(i) - (iw / Math.max(1, months.length)) / 2} y={PAD.t} width={iw / Math.max(1, months.length)} height={ih} fill="transparent" />
            <circle cx={x(i)} cy={y(get(m))} r={4} fill={color} className="stroke-surface" strokeWidth={2} />
            {(months.length <= 14 || i % 3 === 0) && <text x={x(i)} y={H - 8} textAnchor="middle" className="fill-ink-subtle" fontSize={9}>{monthLabel(m.month, months.length > 14)}</text>}
          </g>
        ))}
      </svg>
      {hm && (
        <div className="pointer-events-none absolute right-2 top-1 rounded-md border border-line bg-surface px-2.5 py-1.5 text-[11px] shadow">
          <div className="font-semibold text-ink">{monthLabel(hm.month, true)}</div>
          <div className="text-ink-2"><span className="mr-1 inline-block h-2 w-2 rounded-sm" style={{ background: color }} />Open {get(hm)}</div>
        </div>
      )}
    </div>
  );
}

function Legend({ items }: { items: Array<{ label: string; color: string }> }) {
  return (
    <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-ink-3">
      {items.map((it) => <span key={it.label} className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: it.color }} />{it.label}</span>)}
    </div>
  );
}
